import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { translationService } from '@/services/rest';
import { TranslationEnum } from '@/types/enums/translation.enum';
import { LangHelper } from '@/utils/lang';

interface TranslationState {
    lang: TranslationEnum;
    translations: Record<string, string>;
    loading: boolean;
    error?: string;
}

export const fetchTranslations = createAsyncThunk(
    'translation/fetch',
    async (lang: string, { rejectWithValue }) => {
        try {
            const res = await translationService().keyValue({
                languageCode: lang,
            });

            if (!res.ok) {
                return rejectWithValue('Failed to fetch translation');
            }

            return await res.json();
        } catch {
            return rejectWithValue('Failed to fetch translation');
        }
    }
);

const initialState: TranslationState = {
    lang: TranslationEnum.VI,
    translations: {},
    loading: false,
    error: undefined,
};

const translationSlice = createSlice({
    name: 'translation',
    initialState,
    reducers: {
        setLanguage(state, action: PayloadAction<TranslationEnum>) {
            state.lang = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchTranslations.pending, state => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(fetchTranslations.fulfilled, (state, { payload }) => {
                state.translations = payload.translations;
                LangHelper.setDynamic(payload.translations, state.lang);
                state.loading = false;
            })
            .addCase(fetchTranslations.rejected, (state, { payload }) => {
                state.loading = false;
                state.error =
                    typeof payload === 'string'
                        ? payload
                        : 'Không thể tải bản dịch';
            });
    },
});

export const { setLanguage } = translationSlice.actions;
export default translationSlice.reducer;
